import React, { Component } from "react"
import { Form, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Label, Button, Input, FormFeedback, Alert, Progress, UncontrolledTooltip } from "reactstrap"
import Select from "react-select"
import gql from 'graphql-tag'
import { graphql } from '@apollo/client/react/hoc'
import { AllCarModels, AllAgreementTypes } from "./Queries"
import { CreatePricingTemplateMutation } from './Mutations'
import toTitleCase from "../Functions/toTitleCase"
import withApolloClient from '../withApolloClient'
import PricingConfirmationModal from './PricingConfirmationModal'
import './AddPricingModal.css'

const IntervalUnitOptions = ['Hour', 'Day', 'Week', 'Month', 'Year', 'Mile', 'Trip']
const DisabledIntervalUnits = ['Year', 'Mile', 'Trip']

const UserSelectedBranchesQuery = gql`
  query UserSelectedBranchesForPricing {
    currentUser {
      id
      member {
        id
        selectedBranches {
          edges {
            node {
              id
              name
              defaultMileageUnit
            }
          }
        }
      }
    }
  }
`

class AddPricingModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            input: {
                price: '',
                minimumPrice: '',
                maximumPrice: '',
                interval: '',
                intervalUnit: '',
                carYearMin: '',
                carYearMax: '',
                carMileageMin: '',
                carMileageMax: '',
                visibleToCustomers: true,
                isDefault: false,
                carModels: null,
                branchId: null,
                title: '',
            },
            defaultMileageUnit: "MI",
            createAnother: false,
            loading: false,
            error: "",
            selectedAgreementTypeOption: null,
            selectedBranchOption: null,
            selectedCarModels: null,
            showConfirmationModal: false,
        }
    }
    componentDidMount() {
        // Component initialization
    }

    resetForm = () => {
        this.setState({
            input: {
                price: '',
                minimumPrice: '',
                maximumPrice: '',
                interval: '',
                intervalUnit: '',
                carYearMin: '',
                carYearMax: '',
                carMileageMin: '',
                carMileageMax: '',
                visibleToCustomers: true,
                isDefault: false,
                carModels: null,
                branchId: null,
                title: '',
            },
            defaultMileageUnit: "MI",
            error: "",
            selectedAgreementTypeOption: null,
            selectedBranchOption: null,
            selectedCarModels: null,
        })
    }

    handleSubmit = (e) => {
        e.preventDefault()
        let input = this.state.input

        // Validate required fields
        if (!input.title || !input.title.trim() || !input.price || !input.minimumPrice || !input.maximumPrice || !input.interval || !input.intervalUnit) {
            this.setState({ loading: false, error: "Please provide all required fields:\nTitle, Price, Minimum Price, Maximum Price, Interval, and Interval Unit!" })
            return
        }

        // Check if branch is selected (required first)
        if (!input.branchId) {
            this.setState({ loading: false, error: "Please select a Branch first!" })
            return
        }

        // Agreement Type and every car attribute are optional - each one left empty
        // widens the template to everything on that axis, up to the whole branch

        // Validate year range
        if (input.carYearMin && input.carYearMax && input.carYearMin > input.carYearMax) {
            this.setState({ loading: false, error: "Car Year Min must be less than Car Year Max!" })
            return
        }

        // Validate mileage range
        if (input.carMileageMin && input.carMileageMax && parseInt(input.carMileageMin) > parseInt(input.carMileageMax)) {
            this.setState({ loading: false, error: "Car Mileage Min must be less than Car Mileage Max!" })
            return
        }

        // If validation passes, show confirmation modal
        this.setState({ showConfirmationModal: true, error: "" })
    };

    handleConfirmCreate = () => {
        let input = this.state.input

        try {
            input = {
                ...input,
                price: parseFloat(input.price),
                minimumPrice: parseFloat(input.minimumPrice),
                maximumPrice: parseFloat(input.maximumPrice),
                interval: parseInt(input.interval),
                carYearMin: parseInt(input.carYearMin),
                carYearMax: parseInt(input.carYearMax),
                carMileageMin: input.carMileageMin ? { mileage: parseFloat(input.carMileageMin), mileageUnit: this.state.defaultMileageUnit === "KM" ? "KM" : "MI" } : null,
                carMileageMax: input.carMileageMax ? { mileage: parseFloat(input.carMileageMax), mileageUnit: this.state.defaultMileageUnit === "KM" ? "KM" : "MI" } : null,
            }
            this.setState({ loading: true })

            this.props.apolloClient.mutate({
                mutation: CreatePricingTemplateMutation,
                variables: { input },
            }).then((result) => {
                if (result.data && result.data.createPricingTemplate && result.data.createPricingTemplate.ok) {
                    this.props.refetch()
                    this.setState({ showConfirmationModal: false, loading: false, error: "" })
                    if (!this.state.createAnother) {
                        this.props.toggleModal()
                    } else {
                        this.resetForm()
                        this.setState({ createAnother: false })
                    }
                } else if (result.data && result.data.createPricingTemplate && !result.data.createPricingTemplate.ok && result.data.createPricingTemplate.errors && result.data.createPricingTemplate.errors[0]) {
                    this.setState({ loading: false, showConfirmationModal: false, error: result.data.createPricingTemplate.errors[0].messages.toString() })
                } else {
                    this.setState({ loading: false, showConfirmationModal: false, error: "Something went wrong, please contact admin for support!" })
                }
            }).catch((err) => {
                this.setState({ loading: false, showConfirmationModal: false, error: err.message })
            })

        } catch (err) {
            this.setState({ loading: false, error: err })
        }
    };

    handleChange = (e, number = false) => {
        if (e.target.type === "checkbox")
            this.setState({ input: { ...this.state.input, [e.target.name]: !this.state.input[e.target.name] } })
        else if (number) {
            this.setState({ input: { ...this.state.input, [e.target.name]: parseFloat(e.target.value) } })
        }
        else
            this.setState({ input: { ...this.state.input, [e.target.name]: e.target.value } })
    }

    handleBranchChange = (branch) => {
        if (branch) {
            this.setState({
                input: { ...this.state.input, branchId: branch.value, agreementType: null },
                selectedBranchOption: branch,
                selectedAgreementTypeOption: null, // Reset agreement type when branch changes
                defaultMileageUnit: branch.defaultMileageUnit || "MI"
            })
        } else {
            this.setState({
                input: { ...this.state.input, branchId: null, agreementType: null },
                selectedBranchOption: null,
                selectedAgreementTypeOption: null,
                defaultMileageUnit: "MI"
            })
        }
    };

    buildPricingSummary = () => {
        const { input, selectedAgreementTypeOption, selectedBranchOption, defaultMileageUnit } = this.state
        const { allCarModels } = this.props

        const selectedCarModelNames = input.carModels && allCarModels?.edges
            ? allCarModels.edges
                .filter(edge => input.carModels.includes(edge.node.id))
                .map(edge => {
                    const item = edge.node
                    return toTitleCase(item.make) + " " + item.name +
                        (item.series ? " | " + item.series : "") +
                        (item.groupType ? " | " + item.groupType.name : " | No Group Selected")
                })
            : []

        // No agreement type means the price falls back to every agreement type in the branch
        const isFallback = !selectedAgreementTypeOption

        const pricingType = isFallback
            ? "All Agreement Types"
            : selectedAgreementTypeOption.label

        const branch = selectedBranchOption?.label || null

        return {
            isFallback,
            pricingType,
            branch,
            price: `$${input.price} per ${input.interval} ${input.intervalUnit}`,
            priceRange: `$${input.minimumPrice} - $${input.maximumPrice}`,
            title: input.title || null,
            carModels: selectedCarModelNames,
            yearRange: input.carYearMin != null || input.carYearMax != null
                ? `${input.carYearMin ?? 'Any'} - ${input.carYearMax ?? 'Any'}`
                : null,
            mileageRange: input.carMileageMin != null || input.carMileageMax != null
                ? `${input.carMileageMin ?? 0} - ${input.carMileageMax ?? 'Any'} ${defaultMileageUnit}`
                : null,
            visibleToCustomers: input.visibleToCustomers,
            isDefault: input.isDefault
        }
    };

    handleAgreementTypeChange = (agreementType) => {
        // Since branch is always selected first, we just need to set agreementType
        // null when cleared, which prices every agreement type in the branch
        this.setState({
            input: {
                ...this.state.input,
                agreementType: agreementType?.value || null
            },
            selectedAgreementTypeOption: agreementType || null,
        })
    };

    handleCarModelChange = (selectedModels) => {
        const carModelIds = selectedModels?.length > 0
            ? selectedModels.map(model => model.value)
            : null

        this.setState({
            selectedCarModels: selectedModels,
            input: {
                ...this.state.input,
                carModels: carModelIds
            }
        })
    };


    render() {
        const input = this.state.input
        const mileageLabel = this.state.defaultMileageUnit === "MI" ? "Miles" : "Kilometers"

        const branchOptions = this.props.userSelectedBranches?.edges?.map(({ node }) => ({
            value: node.id,
            label: node.name,
            defaultMileageUnit: node.defaultMileageUnit || "MI"
        })) || []

        const agreementOptions = this.props.allAgreementTypes &&
            this.props.allAgreementTypes.edges &&
            this.props.allAgreementTypes.edges.length > 0 &&
            this.state.selectedBranchOption &&
            // Filter agreement types by selected branch
            this.props.allAgreementTypes.edges
                .filter((item) => item.node.branch && item.node.branch.id === this.state.selectedBranchOption.value)
                .map((item) => ({
                    value: item.node.id,
                    label: item.node.name,
                    branch: item.node.branch || null,
                    branchId: item.node.branch ? item.node.branch.id : null,
                    defaultMileageUnit: (item.node.branch && item.node.branch.defaultMileageUnit) || "MI",
                }))

        const carModelOptions = this.props.allCarModels && this.props.allCarModels.edges.length > 0 && this.props.allCarModels.edges.filter(item => item.node.make && item.node.name).map((item) => ({
            value: item.node.id, label: toTitleCase(item.node.make) + " " + item.node.name + (item.node.series ? " | " + item.node.series : "") + (item.node.groupType ? " | " + item.node.groupType.name : " | No Group Selected")
        }))

        const cadence = input.intervalUnit ? `per ${input.interval || 1} ${input.intervalUnit.toLowerCase()}` : "per interval"
        const selectedModelCount = this.state.selectedCarModels ? this.state.selectedCarModels.length : 0
        const yearRange = input.carYearMin || input.carYearMax ? `${input.carYearMin || "Any"} – ${input.carYearMax || "Any"}` : null
        const mileageRange = input.carMileageMin || input.carMileageMax
            ? `${input.carMileageMin || 0} – ${input.carMileageMax || "Any"} ${this.state.defaultMileageUnit}`
            : null
        // Every filter is optional - each one left empty widens the template to everything on that axis
        const vehicleFilters = [
            selectedModelCount ? `${selectedModelCount} model${selectedModelCount > 1 ? "s" : ""}` : "All models",
            yearRange ? `Year ${yearRange}` : "All years",
            mileageRange ? `Mileage ${mileageRange}` : "Any mileage",
        ]

        return (
            <Modal isOpen={this.props.isOpen} className="pricing-modal">
                <ModalHeader
                    close={(
                        <button type="button" className="pricing-modal__close" aria-label="Close" onClick={() => this.props.toggleModal()}>
                            ×
                        </button>
                    )}
                >
                    <span className="pricing-modal__title">Add New Pricing Templates</span>
                    <span className="pricing-modal__subtitle">Set the rate, placement, and eligible vehicles.</span>
                </ModalHeader>
                <Form onSubmit={this.handleSubmit}>
                    <ModalBody>
                        {this.state.error && <Alert color="danger">{this.state.error}</Alert>}
                        {(this.state.loading || this.props.loading) && <Progress animated color="info" value="100" />}

                        <div className="pricing-modal__layout">
                            <div className="pricing-modal__form">
                                <section className="pricing-modal__section">
                                    <div className="pricing-modal__section-heading">
                                        <span>1</span>
                                        <div>
                                            <h2>Template Basics</h2>
                                            <p className="pricing-modal__subtitle">Name the template and choose how it is exposed.</p>
                                        </div>
                                    </div>
                                    <div className="pricing-modal__grid">
                                        <FormGroup className="pricing-modal__field pricing-modal__span-12">
                                            <Label for="title">Pricing Template Title *</Label>
                                            <Input type="text" name="title" id="title" placeholder="Standard weekly rate" value={input.title} onChange={this.handleChange} />
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <Label for="isDefault">Default Template</Label>
                                            <Label className="pricing-modal__switch" for="isDefault">
                                                <span>Is Default</span>
                                                <input
                                                    className="pricing-modal__toggle"
                                                    id="isDefault"
                                                    name="isDefault"
                                                    type="checkbox"
                                                    checked={input.isDefault}
                                                    onChange={this.handleChange}
                                                />
                                            </Label>
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <Label for="visibleToCustomers">Customer Visibility</Label>
                                            <Label className="pricing-modal__switch" for="visibleToCustomers">
                                                <span>Visible during booking</span>
                                                <input
                                                    className="pricing-modal__toggle"
                                                    id="visibleToCustomers"
                                                    name="visibleToCustomers"
                                                    type="checkbox"
                                                    checked={input.visibleToCustomers}
                                                    onChange={this.handleChange}
                                                />
                                            </Label>
                                        </FormGroup>
                                    </div>
                                </section>

                                <section className="pricing-modal__section">
                                    <div className="pricing-modal__section-heading">
                                        <span>2</span>
                                        <div>
                                            <h2>Placement</h2>
                                            <p className="pricing-modal__subtitle">Pick the branch first, then the agreement it applies to.</p>
                                        </div>
                                    </div>
                                    <div className="pricing-modal__grid">
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <Label for="branch">Branch *</Label>
                                            <Select
                                                inputId="branch"
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                isLoading={this.props.loadingUserBranches}
                                                options={branchOptions}
                                                placeholder="Select Branch"
                                                value={this.state.selectedBranchOption}
                                                onChange={this.handleBranchChange}
                                            />
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <div className="pricing-modal__label-row">
                                                <Label for="agreementType">Agreement Type</Label>
                                                <span>Optional</span>
                                            </div>
                                            <Select
                                                inputId="agreementType"
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                isClearable
                                                isLoading={!(this.props.allAgreementTypes && this.props.allAgreementTypes.edges && this.props.allAgreementTypes.edges.length > 0)}
                                                isDisabled={!this.state.selectedBranchOption}
                                                options={agreementOptions}
                                                placeholder="Select Agreement Type"
                                                value={this.state.selectedAgreementTypeOption}
                                                onChange={(agreementType) => this.handleAgreementTypeChange(agreementType)}
                                            />
                                            <p className="pricing-modal__helper">Leave empty to apply this price to all agreement types in the branch.</p>
                                        </FormGroup>
                                    </div>
                                </section>

                                <section className="pricing-modal__section">
                                    <div className="pricing-modal__section-heading">
                                        <span>3</span>
                                        <div>
                                            <h2>Rate &amp; Cadence</h2>
                                            <p className="pricing-modal__subtitle">Set the allowed range and how often it bills.</p>
                                        </div>
                                    </div>
                                    <div className="pricing-modal__grid">
                                        <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                            <Label for="templatePrice">Default Price *</Label>
                                            <Input type="number" step="any" name="price" id="templatePrice" placeholder="$ Price" min={0} value={input.price} onChange={(e) => this.handleChange(e, true)} />
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                            <Label for="templateMinimumPrice">Minimum Price *</Label>
                                            <Input valid={input.price && input.minimumPrice && input.minimumPrice < input.price}
                                                invalid={input.price && input.minimumPrice && input.minimumPrice > input.price}
                                                type="number" step="any" name="minimumPrice" id="templateMinimumPrice" placeholder="$ Minimum Price" min={0}
                                                value={input.minimumPrice} onChange={(e) => this.handleChange(e, true)} />
                                            <FormFeedback>Minimum Price must be less than the default Price</FormFeedback>
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                            <Label for="templatemaximumPrice">Maximum Price *</Label>
                                            <Input
                                                valid={input.price && input.maximumPrice && input.maximumPrice > input.price}
                                                invalid={input.price && input.maximumPrice && input.maximumPrice < input.price}
                                                type="number" step="any" name="maximumPrice" id="templatemaximumPrice" placeholder="$ Maximum Price" min={0}
                                                value={input.maximumPrice} onChange={(e) => this.handleChange(e, true)} />
                                            <FormFeedback>Maximum Price must be higher than the default Price</FormFeedback>
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <Label for="templateInterval">Interval *</Label>
                                            <Input type="number" name="interval" id="templateInterval" placeholder="Interval" min="1" max="9" value={input.interval} onChange={this.handleChange} />
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <Label for="templateIntervalUnit">Interval Unit *</Label>
                                            <Input type="select" name="intervalUnit" id="templateIntervalUnit" placeholder="Interval Unit" value={input.intervalUnit} onChange={this.handleChange}>
                                                <option value='' disabled>Select Interval Unit</option>
                                                {IntervalUnitOptions.map(item => <option key={item} value={item} disabled={DisabledIntervalUnits.includes(item)}>{item}</option>)}
                                            </Input>
                                        </FormGroup>
                                    </div>
                                </section>

                                <section className="pricing-modal__section">
                                    <div className="pricing-modal__section-heading">
                                        <span>4</span>
                                        <div>
                                            <h2>Vehicle Eligibility</h2>
                                            <p className="pricing-modal__subtitle">Narrow the template, or leave these empty to price every vehicle in the branch.</p>
                                        </div>
                                    </div>
                                    <div className="pricing-modal__grid">
                                        <FormGroup className="pricing-modal__field pricing-modal__span-12">
                                            <div className="pricing-modal__label-row">
                                                <Label for="carModel">Car Model</Label>
                                                <span>Optional</span>
                                            </div>
                                            <Select
                                                inputId="carModel"
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                isLoading={!(this.props.allCarModels && this.props.allCarModels.edges && this.props.allCarModels.edges.length > 0)}
                                                isMulti
                                                value={this.state.selectedCarModels}
                                                options={carModelOptions}
                                                placeholder="Select Model"
                                                onChange={this.handleCarModelChange}
                                            />
                                            <p className="pricing-modal__helper">Leave empty to apply this price to all car models.</p>
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <div className="pricing-modal__label-row">
                                                <Label for="carYearMin">Car Year Min</Label>
                                                <span>Optional</span>
                                            </div>
                                            <Input type="number" name="carYearMin" id="carYearMin" placeholder="Car Year Minimum" min={2000} max={3000} value={input.carYearMin} onChange={this.handleChange} />
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                            <div className="pricing-modal__label-row">
                                                <Label for="carYearMax">Car Year Max</Label>
                                                <span>Optional</span>
                                            </div>
                                            <Input type="number" name="carYearMax" id="carYearMax" placeholder="Car Year Maximum" min={input.carYearMin ? input.carYearMin : 2000} max={3000} value={input.carYearMax} onChange={this.handleChange} />
                                            <p className="pricing-modal__helper">Leave both year fields empty to apply to all car years.</p>
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-2">
                                            <Label for="mileageUnit">Unit</Label>
                                            <Input type="text" name="mileageUnit" id="mileageUnit" disabled value={this.state.defaultMileageUnit} />
                                            <UncontrolledTooltip placement="top" target="mileageUnit">{mileageLabel}</UncontrolledTooltip>
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-5">
                                            <div className="pricing-modal__label-row">
                                                <Label for="carMileageMin">Car Mileage Min</Label>
                                                <span>Optional</span>
                                            </div>
                                            <Input
                                                type="number"
                                                name="carMileageMin"
                                                id="carMileageMin"
                                                placeholder={`Car Minimum ${mileageLabel}`}
                                                min={0}
                                                value={input.carMileageMin}
                                                onChange={this.handleChange}
                                            />
                                        </FormGroup>
                                        <FormGroup className="pricing-modal__field pricing-modal__span-5">
                                            <div className="pricing-modal__label-row">
                                                <Label for="carMileageMax">Car Mileage Max</Label>
                                                <span>Optional</span>
                                            </div>
                                            <Input
                                                type="number"
                                                name="carMileageMax"
                                                id="carMileageMax"
                                                placeholder={`Car Maximum ${mileageLabel}`}
                                                min={input.carMileageMin ? input.carMileageMin : 0}
                                                value={input.carMileageMax}
                                                onChange={this.handleChange}
                                            />
                                            <p className="pricing-modal__helper">Leave both mileage fields empty to apply to any mileage.</p>
                                        </FormGroup>
                                    </div>
                                </section>
                            </div>

                            <aside className="pricing-modal__summary" aria-label="Pricing summary">
                                <h2>Pricing Summary</h2>
                                <p className="pricing-modal__subtitle">Updates as values are entered.</p>
                                <div className="pricing-modal__summary-card">
                                    <div className={input.title ? "pricing-modal__summary-value pricing-modal__summary-value--titled" : "pricing-modal__summary-value"}>
                                        <small>{input.title ? "Template" : "Default Price"}</small>
                                        <strong>
                                            {input.title || `$${input.price || "—"}`}
                                            <span>{input.title ? `$${input.price || "—"} ${cadence}` : cadence}</span>
                                        </strong>
                                    </div>
                                    <dl>
                                        <div>
                                            <dt>Range</dt>
                                            <dd>{input.minimumPrice || input.maximumPrice ? `$${input.minimumPrice || "—"} – $${input.maximumPrice || "—"}` : "Not set"}</dd>
                                        </div>
                                        <div>
                                            <dt>Branch</dt>
                                            <dd>{this.state.selectedBranchOption ? this.state.selectedBranchOption.label : "Not selected"}</dd>
                                        </div>
                                        <div>
                                            <dt>Agreement</dt>
                                            <dd>{this.state.selectedAgreementTypeOption ? this.state.selectedAgreementTypeOption.label : "All agreement types"}</dd>
                                        </div>
                                        <div>
                                            <dt>Vehicle Filters</dt>
                                            <dd>{vehicleFilters.join(" · ")}</dd>
                                        </div>
                                    </dl>
                                </div>
                                <div className="pricing-modal__badges" aria-label="Pricing flags">
                                    {input.isDefault && <span>Default template</span>}
                                    {input.visibleToCustomers && <span>Visible during booking</span>}
                                </div>
                            </aside>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <label className="pricing-modal__create-another">
                            <input
                                className="pricing-modal__checkbox"
                                name="createAnother"
                                type="checkbox"
                                checked={this.state.createAnother}
                                onChange={() => this.setState({ createAnother: !this.state.createAnother })}
                            />
                            Create another
                        </label>
                        <div className="pricing-modal__actions">
                            <Button color="secondary" type="button" onClick={() => this.props.toggleModal()}>Close</Button>
                            <Button color="primary" type="submit" disabled={this.state.loading}>Create Price</Button>
                        </div>
                    </ModalFooter>
                </Form>
                {this.state.showConfirmationModal && (
                    <PricingConfirmationModal
                        isOpen={this.state.showConfirmationModal}
                        toggle={() => this.setState({ showConfirmationModal: false })}
                        onConfirm={this.handleConfirmCreate}
                        pricingSummary={this.buildPricingSummary()}
                        loading={this.state.loading}
                    />
                )}
            </Modal>
        )
    }
}



//-----------------------------------
export default graphql(AllCarModels, {
    options: { fetchPolicy: "cache-first" },
    props: ({ data: { loading, allCarModels } }) => ({ loading, allCarModels })
})(
    graphql(AllAgreementTypes, {
        options: { fetchPolicy: "cache-first" },
        props: ({ data: { loading, allAgreementTypes } }) => ({ loading, allAgreementTypes })
    })(
        graphql(UserSelectedBranchesQuery, {
            options: { fetchPolicy: "cache-first" },
            props: ({ data: { loading, currentUser } }) => ({
                loadingUserBranches: loading,
                userSelectedBranches: currentUser?.member?.selectedBranches
            })
        })(withApolloClient(AddPricingModal))
    )
)
